import { endPoint } from '@/constant/end-point'
import useAPI from '@/hooks/use-api'
import useApiMutation from '@/hooks/use-api-mutation'
import { useActiveOrg } from '@/hooks/use-active-org'
import { useQuery } from '@tanstack/react-query'
import { useCallback } from 'react'

export interface IOrganisationPreferenceListItem {
  id: string
  key: string
  value: string
}

interface ICreateOrganisationPreference {
  key: string
  value: string
}

interface IUpdateOrganisationPreference extends ICreateOrganisationPreference {
  id: string
}

/**
 * Read-only access to the organisation preference list.
 *
 * Kept separate from {@link useOrganisationPreference} so display-only consumers
 * (badges, dropdown scopes) can read a preference without building mutations. Every
 * consumer shares the same query key, so the list is fetched once per organisation.
 */
export const useOrganisationPreferences = () => {
  const { get } = useAPI<IOrganisationPreferenceListItem>()
  const { activeOrg } = useActiveOrg()

  const { data, isLoading, error } = useQuery({
    queryKey: [endPoint.organisation.organisationPreferences, 'list'],
    queryFn: () => get(`${endPoint.organisation.organisationPreferences}/list`),
    // Admin users have no organisation context, so there is nothing to read
    enabled: Boolean(activeOrg?.id),
  })

  const organisationPreferences = data?.data as unknown as IOrganisationPreferenceListItem[] | undefined

  const getOrganisationPreference = useCallback(
    (key: string): IOrganisationPreferenceListItem | null =>
      organisationPreferences?.find((preference) => preference.key === key) ?? null,
    [organisationPreferences]
  )

  const getOrganisationPreferenceValue = useCallback(
    <T extends string>(key: string, fallback: T): T =>
      (organisationPreferences?.find((preference) => preference.key === key)?.value as T) || fallback,
    [organisationPreferences]
  )

  return {
    organisationPreferences,
    getOrganisationPreference,
    getOrganisationPreferenceValue,
    isLoading,
    error,
  }
}

/**
 * Hook for reading and writing organisation-wide preferences (key/value).
 *
 * Unlike the user preference endpoint, the organisation preference API has no
 * upsert: creating an existing key conflicts and updating needs its id, so the
 * key is looked up in the cached list first.
 *
 * Reads are open to any org user; writes require ORG_ADMIN or ORG_MANAGER.
 *
 * @example
 * const { getOrganisationPreference, upsertOrganisationPreference } = useOrganisationPreference()
 * await upsertOrganisationPreference(OrganisationPreferenceKey.POLICY_SELECTION_STATUS_SCOPE, 'APPROVED_ONLY')
 */
export const useOrganisationPreference = () => {
  const { organisationPreferences, getOrganisationPreference, getOrganisationPreferenceValue, isLoading, error } =
    useOrganisationPreferences()

  const { createMutation, updateMutation } = useApiMutation<
    ICreateOrganisationPreference,
    IUpdateOrganisationPreference
  >({
    endpoint: endPoint.organisation.organisationPreferences,
  })

  const upsertOrganisationPreference = useCallback(
    async (key: string, value: string) => {
      const existing = organisationPreferences?.find((preference) => preference.key === key)

      if (existing) await updateMutation.mutateAsync({ id: existing.id, key, value })
      else await createMutation.mutateAsync({ key, value })
    },
    [organisationPreferences, createMutation, updateMutation]
  )

  return {
    organisationPreferences,
    getOrganisationPreference,
    getOrganisationPreferenceValue,
    upsertOrganisationPreference,
    isLoading,
    error,
    isUpserting: createMutation.isPending || updateMutation.isPending,
  }
}
